import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  query,
  where,
  updateDoc,
  getDocs
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable } from 'rxjs';
import { Guardia } from '../models/guardia.model';

interface CrearGuardiaPayload {
  residencialId: string;
  nombre: string;
  correo: string;
  idNacional?: string;
  telefono?: string;
  permitirLogin: boolean;
}

interface CrearGuardiaResult {
  guardiaId: string;
}

interface ActualizarGuardiaPayload {
  guardiaId: string;
  nombre: string;
  correo: string;
  idNacional?: string;
  telefono?: string;
  permitirLogin: boolean;
}

interface ActualizarGuardiaResult {
  guardiaId: string;
}

interface EliminarGuardiaPayload {
  guardiaId: string;
}

interface EliminarGuardiaResult {
  guardiaId: string;
}

@Injectable({ providedIn: 'root' })
export class GuardiaService {
  private firestore = inject(Firestore);
  private storage   = inject(Storage);
  private functions = inject(Functions);

  // ── Lectura — Admin ───────────────────────────────────────────────────────

  // listener en tiempo real — usado por ResidencialStateService para el signal compartido
  getAll(residencialId: string): Observable<Guardia[]> {
    const col = collection(this.firestore, `residenciales/${residencialId}/guardias`);
    const q   = query(col, where('activo', '==', true));
    return collectionData(q, { idField: 'id' }) as Observable<Guardia[]>;
  }

  // lectura puntual — usado en el CRUD como fallback si el signal aún no cargó el guardia
  getById(residencialId: string, id: string): Observable<Guardia> {
    const r = doc(this.firestore, `residenciales/${residencialId}/guardias/${id}`);
    return docData(r, { idField: 'id' }) as Observable<Guardia>;
  }

  // ── Cloud Functions — Admin (escritura vía CF) Auth y Firestore desde la función ────────────────────────────

  // crea Auth user + custom claims + docs en usuarios y guardias — usado en CRUD
  async crearGuardia(payload: CrearGuardiaPayload): Promise<CrearGuardiaResult> {
    const fn = httpsCallable<CrearGuardiaPayload, CrearGuardiaResult>(
      this.functions, 'crearGuardia'
    );
    const result = await fn(payload);
    return result.data;
  }

  // actualiza Auth + usuarios + guardias — usado en CRUD (modo editar)
  async actualizarGuardia(payload: ActualizarGuardiaPayload): Promise<ActualizarGuardiaResult> {
    const fn = httpsCallable<ActualizarGuardiaPayload, ActualizarGuardiaResult>(
      this.functions, 'actualizarGuardia'
    );
    const result = await fn(payload);
    return result.data;
  }

  // soft delete: activo = false en usuarios y guardias — usado en CRUD (btn eliminar)
  async eliminarGuardia(guardiaId: string): Promise<void> {
    const fn = httpsCallable<EliminarGuardiaPayload, EliminarGuardiaResult>(
      this.functions, 'eliminarGuardia'
    );
    await fn({ guardiaId });
  }

  // ── Firestore directo — Admin (escritura) ─────────────────────────────────

  // actualización parcial directa — usado en CRUD para guardar fotosUrl tras subir imágenes
  async update(residencialId: string, id: string, data: Partial<Omit<Guardia, 'id' | 'creadoEn'>>): Promise<void> {
    const r = doc(this.firestore, `residenciales/${residencialId}/guardias/${id}`);
    await updateDoc(r, { ...data });
  }

  // ── Storage — Admin ───────────────────────────────────────────────────────

  // sube imágenes a Storage y devuelve sus URLs — usado en CRUD al guardar con fotos nuevas
  async uploadFotos(
    residencialId: string,
    guardiaId: string,
    files: File[]
  ): Promise<string[]> {
    const urls: string[] = [];
    for (const file of files) {
      const ext     = file.name.split('.').pop() ?? 'png';
      const ts      = Date.now();
      const fotoRef = ref(
        this.storage,
        `residenciales/${residencialId}/guardias/${guardiaId}/fotos/${ts}.${ext}`
      );
      await uploadBytes(fotoRef, file);
      urls.push(await getDownloadURL(fotoRef));
    }
    return urls;
  }

  // elimina una foto de Storage por URL — usado en CRUD al quitar fotos en modo editar
  async deleteFoto(url: string): Promise<void> {
    try {
      await deleteObject(ref(this.storage, url));
    } catch { /* ignorar si ya fue eliminada */ }
  }

  // ── Helpers — Admin (validación) ──────────────────────────────────────────

  // valida un solo correo contra usuarios — usado en CRUD antes de crear o al cambiar email
  async emailExiste(email: string, excludeId: string): Promise<boolean> {
    const normalized = email.toLowerCase().trim();
    const col  = collection(this.firestore, 'usuarios');
    const q    = query(col, where('email', '==', normalized));
    const snap = await getDocs(q);
    return snap.docs.some(d => d.id !== excludeId);
  }
}
